import { useLazyQuery } from "@apollo/client/react";
import { memo, useEffect, useMemo, useState } from "react";
import { Grid } from '@mui/material';
import { CustomDialog } from "../../../../../components/customDialog/CustomDialog";
import DataGridGenerico from '../../../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../../../components/dataGrid/columnHelpers';
import { TextInput } from '../../../../../components/textInput/TextInput';
import { GET_PACIENTES_SAP, adaptPacientesSAPArray } from "../../../../../data/graphQL/queries/Contratos/DireccionPaciente/getPacientesSAP";
import { normalizeFilterValue } from "../../../../../helpers/tools";

export const PacienteSAPInput = memo(function PacienteSAPInput({
  handleChange,
  prevValue,
  input,
  idDelegacion,
  codDelegacion,
  origen,
}) {
  const [openModal, setOpenModal] = useState(false);
  const [pacienteSAP, setPacienteSAP] = useState(prevValue || {});
  const codigoNombre = useMemo(() => {
    if (!!prevValue?.codigoSAP)
      return prevValue.codigoSAP + ' - ' + prevValue.nombre;

    if (!!pacienteSAP?.codigoSAP)
      return pacienteSAP.codigoSAP + ' - ' + pacienteSAP.nombre;

    return '';
  }, [prevValue, pacienteSAP]);

  const handleOpenModal = () => {
    fetchPacientes();
    setOpenModal(true);
  };
  const handleCloseModal = () => setOpenModal(false);

  const columns = generateColumnsDefaultConfiguration([
    {
      field: 'codigoSAP',
      headerName: 'Código',
      flex: 0.1
    },
    {
      field: 'nombre',
      headerName: 'Nombre',
      flex: 0.2
    },
    {
      field: 'tipoDocumento',
      headerName: 'Tipo Doc',
      flex: 0.1
    },
    {
      field: 'numeroDocumento',
      headerName: 'Numero',
      flex: 0.2
    },
    {
      field: 'direccion',
      headerName: 'Dirección',
      flex: 0.2
    },
    {
      field: 'provincia',
      headerName: 'Provincia',
      flex: 0.2
    }]);

  const [
    rows,
    totalCount,
    inputBuscar,
    pacientesLoading,
    setInputBuscar,
    handleTextFilter,
    handleTextClean,
    fetchPacientes
  ] =
    useGetPacientesSAP(idDelegacion, origen, codDelegacion);

  useEffect(() => {
    if (pacienteSAP?.codigoSAP) {
      const updatedFields = {
        codigoSAP: pacienteSAP?.codigoSAP,
        nombre: pacienteSAP?.nombre,
        provincia: pacienteSAP?.provincia,
        direccion: pacienteSAP?.direccion,
        localidad: pacienteSAP?.localidad,
        codigoPostal: pacienteSAP?.codigoPostal,
        observacionExtra: pacienteSAP?.observacionExtra
      };

      const lugarRIData = {
        direccionInstitucion: pacienteSAP?.direccion,
        provinciaInstitucion: pacienteSAP?.provincia,
        codigoPostalInstitucion: pacienteSAP?.codigoPostal,
        localidadInstitucion: pacienteSAP?.localidad,
        habitacion: ''
      };

      handleChange(updatedFields, lugarRIData);
    }

    handleCloseModal();
  }, [pacienteSAP]);

  const handleRowClick = (row) => {
    setPacienteSAP(row.row);
  };

  return <>
    <Grid size={{ xs: 100, sm: 50, md: input.col, lg: input.col }}>
      <TextInput
        key={codigoNombre}
        label={input.displayName}
        disabled={input.disabled}
        name={input.name}
        onClick={() => handleOpenModal()}
        placeholder='Buscar'
        value={codigoNombre}
        searchIcon />

    </Grid>
    <CustomDialog
      maxWidth='lg'
      fullWidth
      onCancel={handleCloseModal}
      open={openModal}
      cancelText='Cerrar'
      title='Buscador de paciente en SAP'>

      <p>Haz click sobre un paciente para seleccionar sus datos.</p>
      <Grid
        container
        justifyContent={'space-between'}>

        <Grid sx={{ my: 2.5 }} size={{ xs: 12, md: 6 }}>
          <TextInput
            placeholder='Filtrar'
            value={inputBuscar}
            searchIcon
            onChange={(e) => setInputBuscar(e.target.value)}
            onClearButtonClick={handleTextClean}
            onKeyUp={handleTextFilter} />

        </Grid>
        <Grid size={{ xs: 12 }}>
          <DataGridGenerico
            loading={pacientesLoading}
            hideFooterPagination={false}
            toolbar={false}
            columns={columns}
            rows={rows}
            totalCount={totalCount}
            onRowClick={(row) => {
              handleRowClick(row);
            }} />

        </Grid>
      </Grid>
    </CustomDialog>
  </>;
});

const useGetPacientesSAP = (idDelegacion, origen, codDelegacion) => {
  const [pacientesOriginal, setPacientesOriginal] = useState([]);
  const [filteredPacientes, setFilteredPacientes] = useState([]);

  const [inputBuscar, setInputBuscar] = useState('');

  const [getPacientesSAP, { loading: pacientesLoading }] = useLazyQuery(GET_PACIENTES_SAP, {
    fetchPolicy: 'no-cache'
  });

  const fetchPacientes = async () => {
    try {
      const res = await getPacientesSAP({
        variables: {
          codDelegacion,
          where: {
            and: [
              origen === 'PP' || origen === 'US'
                ? { idPaciente: { eq: 0 } }
                : { idPaciente: { gt: 0 } }
            ]
          }
        }
      });

      const datos = adaptPacientesSAPArray(res?.data?.pacientesSAP) || [];
      setPacientesOriginal(datos);
      setFilteredPacientes(datos);
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Error en getPacientesSAP:", error);
    }
  };

  const handleTextFilter = () => {
    const searchTerm = normalizeFilterValue(inputBuscar);
    if (searchTerm?.length) {
      setFilteredPacientes(pacientesOriginal?.filter((datoOriginal) => {
        const codigoSAP = datoOriginal?.codigoSAP?.toString();
        const nombre = datoOriginal?.nombre?.toString().toUpperCase();
        const direccion = datoOriginal?.direccion?.toString().toUpperCase();
        const numeroDocumento = datoOriginal?.numeroDocumento?.toString().toUpperCase();

        return (
          codigoSAP.includes(searchTerm) ||
          nombre.includes(searchTerm) ||
          direccion.includes(searchTerm) ||
          numeroDocumento.includes(searchTerm));

      }));
    } else {
      setFilteredPacientes(pacientesOriginal);
    }
  };
  const handleTextClean = () => {
    setInputBuscar('');
    setFilteredPacientes(pacientesOriginal);
  };

  return [
    filteredPacientes,
    pacientesOriginal?.length || 0,
    inputBuscar,
    pacientesLoading,
    setInputBuscar,
    handleTextFilter,
    handleTextClean,
    fetchPacientes,
  ];

};